'use client'

import { Box, Button, ClickAwayListener, CircularProgress, InputBase, Paper, Popper, Typography } from '@mui/material'
import { useLocale, useTranslations } from 'next-intl'
import React, { useRef, useState } from 'react'

import { GuestRoomSelector } from '../guestRoomSelector'
import { HotelDateField } from '../hotelDateField'
import { useHotelSearchManager } from './hooks/useHotelSearchManager'

interface DesktopHotelSearchFormProps {
    searchManager: ReturnType<typeof useHotelSearchManager>
    showError: (message: string) => void
}

type ActiveComponent = 'destination' | 'checkIn' | 'checkOut' | 'guests' | null

const BORDER_COLOR = 'grey.300'

const getCellSx = ({
    flex = 1,
    hasDivider = true,
    cursor = 'pointer'
}: {
    flex?: number
    hasDivider?: boolean
    cursor?: string
}) => ({
    display: 'flex',
    alignItems: 'center',
    minHeight: 70,
    flex,
    p: 2,
    minWidth: 0,
    cursor,
    ...(hasDivider && {
        borderLeft: '1px solid rgb(229 231 235)'
    })
})

const DesktopHotelSearchForm: React.FC<DesktopHotelSearchFormProps> = ({ searchManager, showError }) => {
    const locale = useLocale()
    const t = useTranslations('common')
    const [activeComponent, setActiveComponent] = useState<ActiveComponent>(null)

    const {
        destination,
        handleDestinationInputChange,
        handleDestinationSelect,
        destinationSuggestions,
        destinationLoading,
        checkIn,
        checkOut,
        nights,
        roomConfigs,
        handleCheckInChange,
        handleCheckOutChange,
        handleRoomCountChange,
        handleRoomAdultsChange,
        handleRoomChildrenChange,
        minRooms,
        maxRooms,
        minAdultsPerRoom,
        maxAdultsPerRoom,
        minChildrenPerRoom,
        maxChildrenPerRoom,
        isSearchReady,
        minCheckOutDate,
        performHotelSearch
    } = searchManager

    const destinationWrapperRef = useRef<HTMLDivElement | null>(null)

    const isComponentActive = (component: ActiveComponent) => activeComponent === component

    const handleComponentActivation = (component: ActiveComponent) => {
        if (activeComponent === component && component !== 'destination') {
            setActiveComponent(null)
        } else {
            setActiveComponent(component)
        }
    }

    const closeActiveComponent = () => setActiveComponent(null)

    const handleClickAway = () => {
        if (activeComponent) {
            setActiveComponent(null)
        }
    }

    const handleSearch = () => {
        if (!isSearchReady) {
            showError(t('pleaseFillAllRequiredFields'))
            return
        }
        setActiveComponent(null)
        performHotelSearch(locale)
    }

    return (
        <Box sx={{ position: 'relative', zIndex: 0 }} data-home-hotel-search-form>
            <ClickAwayListener onClickAway={handleClickAway}>
                <Box
                    data-search-container
                    sx={{
                        display: 'flex',
                        alignItems: 'stretch',
                        justifyContent: 'flex-start',
                        overflow: 'hidden',
                        border: 1,
                        borderColor: BORDER_COLOR,
                        borderRadius: '0.75rem',
                        position: 'relative',
                        zIndex: 1
                    }}
                >
                    <Box sx={getCellSx({ flex: 1.56, hasDivider: false, cursor: 'text' })}>
                        <Box ref={destinationWrapperRef} sx={{ width: '100%' }}>
                            <Typography variant="caption" color="text.secondary" display="block">
                                {t('destination')}
                            </Typography>
                            <InputBase
                                value={destination}
                                onChange={e => {
                                    handleDestinationInputChange(e.target.value)
                                    setActiveComponent('destination')
                                }}
                                placeholder={t('hotelDestinationPlaceholder')}
                                onFocus={() => handleComponentActivation('destination')}
                                onClick={() => handleComponentActivation('destination')}
                                inputProps={{ style: { fontSize: 14 } }}
                                sx={{
                                    width: '100%',
                                    '& input': {
                                        padding: 0,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap'
                                    }
                                }}
                            />

                            <Popper
                                open={activeComponent === 'destination' && (!!destinationLoading || !!destinationSuggestions.length)}
                                anchorEl={destinationWrapperRef.current}
                                placement="bottom-start"
                                disablePortal={false}
                                style={{ zIndex: 2000, minWidth: 420 }}
                            >
                                <Paper
                                    sx={{
                                        width: '100%',
                                        maxHeight: 420,
                                        overflowY: 'auto',
                                        mt: 1.5,
                                        borderRadius: 2
                                    }}
                                    elevation={4}
                                >
                                    {destinationLoading ? (
                                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                                            <CircularProgress size={22} />
                                        </Box>
                                    ) : destinationSuggestions.length ? (
                                        (() => {
                                            const cities = destinationSuggestions.filter(s => s.kind === 'city')
                                            const hotels = destinationSuggestions.filter(s => s.kind === 'hotel')
                                            const groups: { label: string; items: typeof destinationSuggestions }[] = []
                                            if (cities.length) groups.push({ label: t('city'), items: cities })
                                            if (hotels.length) groups.push({ label: t('hotel'), items: hotels })

                                            return groups.map(group => (
                                                <Box key={group.label}>
                                                    <Typography
                                                        variant="caption"
                                                        sx={{
                                                            display: 'block',
                                                            px: 2,
                                                            pt: 1.5,
                                                            pb: 0.5,
                                                            fontWeight: 700,
                                                            fontSize: 11,
                                                            textTransform: 'uppercase',
                                                            letterSpacing: '0.08em',
                                                            color: 'text.secondary'
                                                        }}
                                                    >
                                                        {group.label}
                                                    </Typography>
                                                    {group.items.map(item => (
                                                        <Box
                                                            key={item.key}
                                                            sx={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                px: 2,
                                                                py: 1.25,
                                                                cursor: 'pointer',
                                                                gap: 1,
                                                                '&:hover': { bgcolor: 'grey.50' }
                                                            }}
                                                            onMouseDown={e => e.stopPropagation()}
                                                            onClick={e => {
                                                                e.stopPropagation()
                                                                handleDestinationSelect(item)
                                                                setActiveComponent(null)
                                                            }}
                                                        >
                                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                                <Typography
                                                                    component="span"
                                                                    sx={{ fontWeight: 600, fontSize: 14, color: 'text.primary' }}
                                                                >
                                                                    {item.text}
                                                                </Typography>
                                                                {(item.provinceName || item.countryName) && (
                                                                    <Typography
                                                                        component="span"
                                                                        sx={{ fontSize: 13, color: 'text.secondary', ml: 0.5 }}
                                                                    >
                                                                        {[item.provinceName, item.countryName].filter(Boolean).join(', ')}
                                                                    </Typography>
                                                                )}
                                                            </Box>
                                                            <Box
                                                                sx={{
                                                                    background: '#f3f4f6',
                                                                    color: '#222',
                                                                    borderRadius: 1,
                                                                    fontWeight: 600,
                                                                    fontSize: '12px',
                                                                    px: 1.5,
                                                                    py: 0.5,
                                                                    flexShrink: 0,
                                                                    textAlign: 'center',
                                                                    minWidth: 40
                                                                }}
                                                            >
                                                                {group.label}
                                                            </Box>
                                                        </Box>
                                                    ))}
                                                </Box>
                                            ))
                                        })()
                                    ) : (
                                        <Box sx={{ px: 2, py: 1.5 }}>
                                            <Typography variant="body2" color="text.secondary">
                                                {t('noAirportFound')}
                                            </Typography>
                                        </Box>
                                    )}
                                </Paper>
                            </Popper>
                        </Box>
                    </Box>

                    <Box sx={getCellSx({ flex: 1 })} onClick={() => handleComponentActivation('checkIn')}>
                        <HotelDateField
                            fieldType="checkIn"
                            value={checkIn}
                            checkIn={checkIn}
                            onChange={handleCheckInChange}
                            active={isComponentActive('checkIn')}
                            onActivate={() => handleComponentActivation('checkIn')}
                            onClose={closeActiveComponent}
                        />
                    </Box>

                    <Box sx={getCellSx({ flex: 1 })} onClick={() => handleComponentActivation('checkOut')}>
                        <HotelDateField
                            fieldType="checkOut"
                            value={checkOut}
                            checkIn={checkIn}
                            minDate={minCheckOutDate}
                            onChange={handleCheckOutChange}
                            active={isComponentActive('checkOut')}
                            onActivate={() => handleComponentActivation('checkOut')}
                            onClose={closeActiveComponent}
                        />
                    </Box>

                    <Box sx={getCellSx({ flex: 0.6, cursor: 'default' })}>
                        <Box sx={{ textAlign: 'center', width: '100%' }}>
                            <Typography variant="caption" color="text.secondary" display="block">
                                {t('nights')}
                            </Typography>
                            <Typography variant="body2" fontWeight={600}>
                                {nights ?? '-'}
                            </Typography>
                        </Box>
                    </Box>

                    <Box sx={getCellSx({ flex: 1 })} onClick={() => handleComponentActivation('guests')}>
                        <GuestRoomSelector
                            roomConfigs={roomConfigs}
                            minRooms={minRooms}
                            maxRooms={maxRooms}
                            minAdultsPerRoom={minAdultsPerRoom}
                            maxAdultsPerRoom={maxAdultsPerRoom}
                            minChildrenPerRoom={minChildrenPerRoom}
                            maxChildrenPerRoom={maxChildrenPerRoom}
                            onRoomCountChange={handleRoomCountChange}
                            onRoomAdultsChange={handleRoomAdultsChange}
                            onRoomChildrenChange={handleRoomChildrenChange}
                            active={isComponentActive('guests')}
                            onActivate={() => handleComponentActivation('guests')}
                            onClose={closeActiveComponent}
                        />
                    </Box>

                    <Box sx={getCellSx({ flex: 0.9, cursor: 'default' })}>
                        <Button
                            variant="customVariantPrimary"
                            sx={{
                                borderRadius: '0.75rem',
                                py: 1.5,
                                px: 2,
                                minWidth: 100,
                                width: '100%',
                                whiteSpace: 'nowrap'
                            }}
                            onClick={handleSearch}
                        >
                            {t('searchHotels')}
                        </Button>
                    </Box>
                </Box>
            </ClickAwayListener>
        </Box>
    )
}

export default DesktopHotelSearchForm
